import React, { useState, Dispatch, SetStateAction } from 'react';
import {
  Typography,
  Button,
  Box,
  Modal,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useSetAtom } from 'jotai';
import {
  isBackdropOpenAtom,
  pageAtom,
  snackbarPropsAtom,
} from '../../../atoms';
import { ApiAccounts } from '../../../api/apiAccounts';
import { PAGE } from '../../../constant/page';

const ModalWrapper = React.forwardRef<
  typeof Box,
  { children: React.ReactNode }
>(({ children }, ref) => {
  return (
    <Box
      ref={ref}
      tabIndex={-1}
      flexDirection={'column'}
      rowGap={3}
      sx={{
        display: 'flex',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: '500px',
        bgcolor: 'background.paper',
        border: 2,
        borderColor: 'black',
        borderRadius: '3px',
      }}
    >
      {children}
    </Box>
  );
});

const ModalHeader = () => {
  return (
    <Box
      display={'flex'}
      minHeight={'50px'}
      height={'10%'}
      maxHeight={'80px'}
      bgcolor={'#ef4f2a'}
      color={'white'}
      alignItems={'center'}
    >
      <Typography variant='h5' marginLeft={2}>
        アカウント削除の確認
      </Typography>
    </Box>
  );
};

const DeleteWarningBox = () => {
  return (
    <Box
      width={'75%'}
      height={'80%'}
      display='flex'
      flexDirection='column'
      alignItems='center'
      borderColor={'gray'}
      border={1}
      gap='30px'
      overflow={'auto'}
      padding={3}
    >
      <Typography variant='h5'>{'このアカウントを削除しますか？'}</Typography>
      <Typography variant='body1' whiteSpace={'pre-line'}>
        {`器械の管理をしたい場合は、再度アカウント登録をし直す必要があります。\n
        本当によろしいですか？`}
      </Typography>
    </Box>
  );
};

const AccountDeleteModal = (props: {
  isAccountDeleteModalOpen: boolean;
  setIsAccountDeleteModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsAccountInfoModalOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const {
    isAccountDeleteModalOpen,
    setIsAccountDeleteModalOpen,
    setIsAccountInfoModalOpen,
  } = props;
  const [isAcceptDelete, setIsAcceptDelete] = useState(false);
  const setIsBackdropOpen = useSetAtom(isBackdropOpenAtom);
  const setSnackbarProps = useSetAtom(snackbarPropsAtom);
  const setPage = useSetAtom(pageAtom);

  const onClickAccountDelete = () => {
    setIsAccountDeleteModalOpen(false);
    setIsAccountInfoModalOpen(false);
    setIsBackdropOpen(true);
    ApiAccounts.delete()
      .then(() => {
        setSnackbarProps({
          isOpen: true,
          type: 'success',
          message: 'アカウントの削除に成功しました。',
        });
        setPage(PAGE.LOGIN);
      })
      .catch((error) => {
        console.log(error);
        setSnackbarProps({
          isOpen: true,
          type: 'error',
          message: 'アカウントの削除に失敗しました。',
        });
      })
      .finally(() => {
        setIsBackdropOpen(false);
      });
  };

  return (
    <Modal
      open={isAccountDeleteModalOpen}
      onClose={() => {
        setIsAccountDeleteModalOpen(!isAccountDeleteModalOpen);
      }}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <ModalWrapper>
        <ModalHeader />
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'flex-start'}
          flexDirection={'column'}
          overflow={'auto'}
          rowGap={5}
        >
          <DeleteWarningBox />
          <FormControlLabel
            control={
              <Checkbox
                checked={isAcceptDelete}
                onChange={() => setIsAcceptDelete(!isAcceptDelete)}
              />
            }
            label={'アカウントを削除する'}
          />

          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'center'}
            columnGap={10}
            marginBottom={5}
          >
            <Button
              variant='contained'
              color={'error'}
              disabled={!isAcceptDelete}
              sx={{ width: '200px' }}
              onClick={onClickAccountDelete}
            >
              {'アカウント削除'}
            </Button>
            <Button
              variant='contained'
              color={'inherit'}
              sx={{ width: '200px' }}
              onClick={() => {
                setIsAcceptDelete(false);
                setIsAccountDeleteModalOpen(false);
              }}
            >
              {'キャンセル'}
            </Button>
          </Box>
        </Box>
      </ModalWrapper>
    </Modal>
  );
};

export default AccountDeleteModal;
